import { ApplicationElement, html } from '@beunity/lit-components';

import '@beunity/lit-components/components/dropdown/element.js';
import '@beunity/lit-components/components/icon/element.js';
import '@beunity/lit-components/components/icon_block/element.js';
import { csrfFetch } from '@beunity/lit-components/lib/helpers.js';
import '../notification/element.js';

import style from './style.lit.css';
import translations from './translations.js';
import orderBy from 'lodash-es/orderBy.js';

/**
 * Component with an icon and dropdown menu.
 *
 * ```js
 * // format of notifications
 * notifications: [
    {
      createdAt: '2022-10-20',
      href: '#',
      isRead: false,
      isPermanent: false,
      picture: portraits.women_40,
      title: 'Unread notifications',
      body: 'Please check your notifications',
    }
  ]
 * ```
 *
 * @prop notifications - Array of notification objects forwarded from outside
 * @prop allNotificationsUrl - Link to list of all notifications
 * @prop unreadNotificationsUrl - Link to unread notification
 * @prop open - Indicates if the dropdown menu is rendered, false by default (optional)
 */
export default class MiniNotifications extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    notifications: { type: Array },
    unreadNotificationsUrl: { type: String },
    markAllAsReadUrl: { type: String },
    allNotificationsUrl: { type: String },
    open: { type: Boolean },
  };

  static lastNotifications = [];

  constructor() {
    super();

    this.open = false;
    this.notifications = this.constructor.lastNotifications;
  }

  async loadNotifications() {
    if (!this.unreadNotificationsUrl) return;

    try {
      const response = await csrfFetch(this.unreadNotificationsUrl);
      const json = await response.json();
      this.notifications = json.notifications;
      this.constructor.lastNotifications = this.notifications;
    } catch {
      // nothing
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadNotifications();
    window.addEventListener('resize', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this);
  }

  handleEvent(e) {
    e.type === 'resize' && this.requestUpdate();
  }

  /** @internal */
  get isSmallDevice() {
    return window.innerWidth < 768;
  }

  /** @internal */
  static translations = translations;

  get unreadCount() {
    return this.notifications.length;
  }

  get sortedNotifications() {
    return orderBy(
      this.notifications,
      ['isPermanent', 'createdAt'],
      ['desc', 'desc']
    );
  }

  async markAllAsRead() {
    const { notifications } = this;
    this.notifications = [];

    try {
      await csrfFetch(this.markAllAsReadUrl, {
        method: 'POST',
      });
    } catch {
      // Revert in case of error
      this.notifications = notifications;
    }
  }

  notificationsRender() {
    return this.sortedNotifications.map(
      notification =>
        html`<bu-notification
          createdAt=${notification.createdAt}
          href=${notification.href}
          isRead=${notification.isRead}
          isPermanent=${notification.isPermanent}
          picture=${notification.picture}>
          <span slot="title">${notification.title}</span>
          ${notification.body}
        </bu-notification>`
    );
  }

  renderIcon() {
    return html`
      <ef-icon name="bell-s" .badgeCount=${this.unreadCount}> </ef-icon>
    `;
  }

  renderFull() {
    return html`
      <ef-dropdown open=${this.open} placement="bottom-end">
        <button slot="trigger" class="icon-btn">${this.renderIcon()}</button>
        <div class="dropdown">
          <header class="menu-header">
            ${this.unreadCount > 0
              ? html` <a class="btn" @click=${this.markAllAsRead}>
                  <ef-icon-block icon="check" text="${this.t('markAllAsRead')}">
                  </ef-icon-block>
                </a>`
              : html` <p>${this.t('noNotifications')}</p> `}
          </header>

          <main>${this.notificationsRender()}</main>

          <footer>
            <a class="btn" href=${this.allNotificationsUrl}>
              <ef-icon-block
                icon="bell"
                text="${this.t('showAll')}"></ef-icon-block>
            </a>
          </footer>
        </div>
      </ef-dropdown>
    `;
  }

  renderOnlyIcon() {
    return html`
      <a href=${this.allNotificationsUrl} class="icon-btn">
        ${this.renderIcon()}
      </a>
    `;
  }

  render() {
    return this.isSmallDevice ? this.renderOnlyIcon() : this.renderFull();
  }
}

MiniNotifications.registerTagName('bu-mini-notifications');

customElements.get('bu-mini-notifications') ||
  customElements.define('bu-mini-notifications', MiniNotifications);
